import React, { useEffect, useState } from 'react';
import { FormulasInterface, IDeal } from 'store/deals/types';
import { ModalContainerDark, OptionModal } from 'components/modal-provider/modal';
import { IFormulaEntryState, IMarker } from 'store/formula-entry/types';
import FormulaInput from 'components/formula-entry/formula';
import MarkerSearchBox from 'components/formula-entry/marker-search-box';
import MarkerSearchResults from 'components/formula-entry/marker-search-results';
import {
  ButtonContainer,
  CancelButton,
  CloseIcon,
  Container,
  Content,
  ContentSection,
  ContentSectionPanel,
  InputError,
  InputGroup,
  Label,
  SaveFormulaButton,
  Title,
  TitleContainer,
} from 'components/formula-entry/modals/_common';
import { isMatch } from 'store/formula-entry/match';
import messages from 'constants/messages';
import {
  validateFormula,
  getUnitLabel,
  triggerDuplicateMarkerPreventionTimer,
} from 'components/formula-entry/modals/helpers';
import { handleError } from 'lib/helpers/error-handler';
import { capitalizeFirstLetter } from 'lib/helpers/string-manipulators';
import { ELoadingType } from 'store/loading/types';
import { IDealProduct, IPrice } from 'store/field-options/types';
import MarkerFilters from '../formula-list/marker-filters';
import { TConcatArraysReturnType } from '../types';

interface IFormula {
  formulaValue: string;
  formulaError?: string | null;
}

interface IFormulaEntryContainerProps {
  dealEdit: IDeal;
  formulaEntry: IFormulaEntryState;
  getDealEvaluation: () => void;
  cancelFormulaEdit: () => void;
  setLoading: (loading: ELoadingType) => void;
  updateProductField: Function;
  allTypesMarkerList: TConcatArraysReturnType[];
  updateMarkerList: Function;
}

function getBasePrice(basePrice: IPrice | undefined) {
  return basePrice ? basePrice.formula : '';
}

function processInsertMarkers(
  name: string,
  timer: number | null,
  setTimer: Function,
  cursorPosition: number,
  setCap: Function,
  cap: IFormula
) {
  // Check for active timer, if found, prevent insertion of duplicate marker
  if (timer) {
    return;
  }

  // Kickoff new timer to handle double-clicking of markers
  triggerDuplicateMarkerPreventionTimer(setTimer);

  const formula = cap.formulaValue;
  const formulaValueUpdated = [formula.slice(0, cursorPosition), `[${name}]`, formula.slice(cursorPosition)].join('');

  setCap({
    ...cap,
    formulaValue: formulaValueUpdated,
  });
}

async function processSave(
  productIndex: number | null,
  cap: IFormula,
  processSaveObj: {
    updateProductField: Function;
    getDealEvaluation: Function;
    cancelFormulaEdit: Function;
    setLoading: Function;
  },
  dealFormulas: FormulasInterface[] | undefined,
  setCap: Function,
  dealEdit: IDeal
) {
  const capAssin = cap;
  if (productIndex === null) {
    handleError(messages.errors.default);
    return;
  }

  const { updateProductField, getDealEvaluation, cancelFormulaEdit, setLoading } = processSaveObj;

  // Trim input value
  capAssin.formulaValue = cap.formulaValue.trim();

  // Check for empty volume -- delete option scenario
  if (!cap.formulaValue) {
    updateProductField(productIndex, 'capValue', null);
    updateProductField(productIndex, 'capEnabled', false);
    getDealEvaluation();
    cancelFormulaEdit();
    return;
  }

  setLoading(ELoadingType.SPINNER);

  let capErrorMessage;

  try {
    if (cap.formulaValue) {
      const { formulaValue: formula } = cap;
      const capErrorResponse = await validateFormula(dealFormulas, [{ formula, name: '' }]);
      const capError = capErrorResponse.data.error;
      if (capError) {
        capErrorMessage = capitalizeFirstLetter(capError.message);
      }
    }
  } catch (e) {
    handleError(e);
    setLoading(ELoadingType.NONE);
  }

  setCap({
    ...cap,
    formulaError: capErrorMessage,
  });

  // Update deal, re-evaluate, and close modal if no errors exist
  if (!capErrorMessage) {
    updateProductField(productIndex, 'capValue', cap.formulaValue);
    updateProductField(productIndex, 'capEnabled', true);
    getDealEvaluation();
    cancelFormulaEdit();
  } else {
    setLoading(ELoadingType.NONE);
  }
}

function handleSearchValueChange(
  e: React.ChangeEvent<HTMLInputElement>,
  availableMarkers: IMarker[] | null,
  setMarkerSearchValue: { (value: React.SetStateAction<string>): void; (arg0: string): void }
) {
  if (!availableMarkers) {
    handleError(messages.errors.noMarkers);
    return;
  }
  const val = e.target.value;
  setMarkerSearchValue(val);
}

function getProductCap(products: IDealProduct[], productIndex: number) {
  return products[productIndex].capValue ?? '';
}

export default ({
  cancelFormulaEdit,
  dealEdit,
  formulaEntry,
  getDealEvaluation,
  setLoading,
  updateProductField,
  allTypesMarkerList,
  updateMarkerList,
}: IFormulaEntryContainerProps) => {
  const { availableMarkers, productIndex, markerList } = formulaEntry;
  const { products, formulas: dealFormulas } = dealEdit;

  const [isInitialized, setIsInitialized] = useState(false);
  const [cap, setCap] = useState<IFormula>({
    formulaValue: '',
    formulaError: null,
  });

  const [cursorPosition, setCursorPosition] = useState(0);
  const [filteredMarkers, setFilteredMarkers] = useState(availableMarkers);
  const [markerSearchValue, setMarkerSearchValue] = useState('');
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    setFilteredMarkers(markerList);
  }, [markerList]);

  // Determine base formula display value
  let baseFormulaValue = '';
  if (products && productIndex !== null) {
    const { basePrice } = products[productIndex];
    baseFormulaValue = getBasePrice(basePrice);
  }

  // Inject marker at cursorPosition
  function insertMarker(name: string) {
    processInsertMarkers(name, timer, setTimer, cursorPosition, setCap, cap);
  }

  const processSaveObj = {
    updateProductField,
    getDealEvaluation,
    cancelFormulaEdit,
    setLoading,
  };

  // Updates cap formula value in product
  async function save() {
    processSave(productIndex, cap, processSaveObj, dealFormulas, setCap, dealEdit);
  }

  useEffect(() => {
    // Initialize formula value
    if (!isInitialized && products && productIndex !== null) {
      setIsInitialized(true);
      setCap({
        ...cap,
        formulaValue: getProductCap(products, productIndex),
      });
    }
  }, [cap, isInitialized, productIndex, products]);

  // Manage filtered marker list
  useEffect(() => {
    if (!availableMarkers) {
      handleError(messages.errors.noMarkers);
      return;
    }
    const markers = markerList || allTypesMarkerList;
    const filteredMarkersNew = markers.filter((marker: IMarker) => isMatch(markerSearchValue, marker.name));
    setFilteredMarkers(filteredMarkersNew);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMarkers, markerList, markerSearchValue]);

  // Update marker value and filtered list

  // Handle clear button for marker search input
  function handleSearchValueClear() {
    setMarkerSearchValue('');
    setFilteredMarkers(availableMarkers);
  }

  return (
    <ModalContainerDark>
      <OptionModal>
        <Container>
          <TitleContainer>
            <Title>Cap Option Entry</Title>
            <CloseIcon onClick={cancelFormulaEdit} />
          </TitleContainer>

          <Content>
            <ContentSection>
              <ContentSectionPanel>
                <InputGroup>
                  <Label>{`BASE FORMULA ${getUnitLabel(products, productIndex).toUpperCase()}`}</Label>
                  <FormulaInput formulaInputValue={baseFormulaValue} readOnly />
                  <InputError />
                </InputGroup>

                <InputGroup>
                  <Label data-valid={!!cap.formulaError}>{`CAP PRICE FORMULA ${getUnitLabel(
                    products,
                    productIndex
                  ).toUpperCase()}`}</Label>
                  <FormulaInput
                    formulaInputValue={cap.formulaValue}
                    isActive
                    onChange={e => setCap({ ...cap, formulaValue: e.target.value })}
                    setCursorPosition={(p: number) => setCursorPosition(p)}
                    setFormula={(value: string) => setCap({ ...cap, formulaValue: value })}
                  />
                  <InputError>{cap.formulaError}</InputError>
                </InputGroup>
              </ContentSectionPanel>
              <ContentSectionPanel>
                <Label>SEARCH FOR MARKER</Label>
                <MarkerSearchBox
                  value={markerSearchValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleSearchValueChange(e, availableMarkers, setMarkerSearchValue)
                  }
                  onClear={handleSearchValueClear}
                />
                <MarkerFilters allTypesMarkerList={allTypesMarkerList} updateMarkerList={updateMarkerList} />
                <MarkerSearchResults markers={filteredMarkers} onClick={insertMarker} />
              </ContentSectionPanel>
            </ContentSection>
          </Content>

          <ButtonContainer>
            <CancelButton onClick={cancelFormulaEdit}>Cancel</CancelButton>
            <SaveFormulaButton onClick={save}>Apply Option</SaveFormulaButton>
          </ButtonContainer>
        </Container>
      </OptionModal>
    </ModalContainerDark>
  );
};
